import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import { StudentLookup, StudentsApi } from '@/services/school.api';
import {
  ClassChangeRequest,
  SchoolClassChangeRequestsApi,
} from '@/services/class-change-requests.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { usePermissions } from '@/hooks/usePermissions';

const STATUS_TONE = { pending: 'amber', approved: 'green', rejected: 'red', partial_failure: 'red' } as const;
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', partial_failure: 'Needs attention' } as const;

/**
 * Cross-school Class Change Request — school tier. Create/edit/withdraw
 * your own PENDING requests; approval happens at the org console
 * (OrgClassChangeRequestsPage), never here. The target must be a sibling
 * school in your own organization — anything else would be a request no
 * org admin could ever approve (approval checks both sides are in-org).
 */
export function ClassChangeRequestsPage() {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canRequest = can('/students', 'create');

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<StudentLookup[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentLookup | null>(null);
  const [newSchoolId, setNewSchoolId] = useState('');
  const [newClassId, setNewClassId] = useState('');
  const [newSectionId, setNewSectionId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: requests = [], isFetching } = useQuery({
    queryKey: ['school-class-change-requests'],
    queryFn: SchoolClassChangeRequestsApi.list,
  });

  const { data: targetSchools = [] } = useQuery({
    queryKey: ['class-change-target-schools'],
    queryFn: SchoolClassChangeRequestsApi.targetSchools,
  });

  const { data: targetClasses = [] } = useQuery({
    queryKey: ['class-change-target-classes', newSchoolId],
    queryFn: () => SchoolClassChangeRequestsApi.targetClasses(newSchoolId),
    enabled: !!newSchoolId,
  });
  const targetSections = targetClasses.find((c) => c.id === newClassId)?.sections ?? [];

  const runSearch = useMutation({
    mutationFn: (q: string) => StudentsApi.lookup({ search: q }),
    onSuccess: (r) => setSearchResults(r),
  });

  const resetForm = () => {
    setSelectedStudent(null);
    setSearch('');
    setSearchResults([]);
    setNewSchoolId('');
    setNewClassId('');
    setNewSectionId('');
    setReason('');
  };

  const create = useMutation({
    mutationFn: () =>
      SchoolClassChangeRequestsApi.create({
        studentId: selectedStudent!.id,
        newSchoolId,
        newSectionId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['school-class-change-requests'] });
      resetForm();
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Failed to submit request'),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => SchoolClassChangeRequestsApi.withdraw(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['school-class-change-requests'] }),
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Failed to withdraw request'),
  });

  const ready = !!selectedStudent && !!newSchoolId && !!newSectionId;

  return (
    <div>
      <PageHeader
        title="Class Change Requests"
        description="Request moving a student to a sibling school in your organization. An org admin approves before anything changes — this only submits the request."
      />

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      {canRequest && (
        <div className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700">New Request</h3>

          <Field label="Student" required>
            {selectedStudent ? (
              <div className="flex items-center justify-between rounded-md border border-slate-300 px-3 py-2 text-sm">
                <span>
                  {selectedStudent.studentName} <span className="text-slate-400">({selectedStudent.admissionNumber})</span>
                </span>
                <button type="button" onClick={() => setSelectedStudent(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && search.trim() && runSearch.mutate(search.trim())}
                      placeholder="Search by name or admission #"
                      className="pl-9"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={!search.trim() || runSearch.isPending}
                    onClick={() => runSearch.mutate(search.trim())}
                  >
                    Search
                  </button>
                </div>
                {searchResults.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-md border border-slate-200">
                    {searchResults.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => {
                          setSelectedStudent(s);
                          setSearchResults([]);
                        }}
                      >
                        <span>{s.studentName}</span>
                        <span className="text-slate-400">{s.admissionNumber}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="Target School" required>
              <Select
                value={newSchoolId}
                onChange={(e) => {
                  setNewSchoolId(e.target.value);
                  setNewClassId('');
                  setNewSectionId('');
                }}
              >
                <option value="">Select</option>
                {targetSchools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Target Class" required>
              <Select
                value={newClassId}
                onChange={(e) => {
                  setNewClassId(e.target.value);
                  setNewSectionId('');
                }}
                disabled={!newSchoolId}
              >
                <option value="">Select</option>
                {targetClasses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Target Section" required>
              <Select value={newSectionId} onChange={(e) => setNewSectionId(e.target.value)} disabled={!newClassId}>
                <option value="">Select</option>
                {targetSections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {targetSchools.length === 0 && (
            <p className="text-sm text-slate-400">No sibling schools found in your organization.</p>
          )}

          <Field label="Reason" hint="Optional — helps the approving admin.">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </Field>

          <div className="flex justify-end">
            <button
              type="button"
              className="btn-primary"
              disabled={!ready || create.isPending}
              onClick={() => {
                setError(null);
                create.mutate();
              }}
            >
              {create.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
              Submit Request
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Student</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">From</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">To</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Remarks</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  No class change requests yet.
                </td>
              </tr>
            ) : (
              requests.map((r: ClassChangeRequest) => (
                <tr key={r.id}>
                  <td className="px-4 py-2.5 text-sm text-slate-800">
                    {r.studentName} <span className="text-slate-400">({r.admissionNo})</span>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">
                    {r.currentClassName} / {r.currentSectionName}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">
                    {r.newClassName} / {r.newSectionName}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{r.remarks || r.partialFailureNote || '—'}</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.status === 'pending' && canRequest && (
                      <button
                        type="button"
                        className="text-sm text-red-500 hover:text-red-700"
                        disabled={withdraw.isPending}
                        onClick={() => {
                          setError(null);
                          withdraw.mutate(r.id);
                        }}
                      >
                        Withdraw
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
